import { useTranslation } from "@coworker/locales";
import CalendarIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CalendarIconPath";
import { useCallback, useState } from "react";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import {
  getScheduledTaskDescription,
  getScheduleTypeTitle,
  ScheduleType,
} from "../helper";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FutureTask } from "@coworker/apprestructured/src/task-management/components/ScheduleTask/ScheduleModal/FutureTask/FutureTask";

type EditTaskScheduleProps = {
  disabled: boolean;
  start: string | null;
  onConfirmClick: (start: string | null) => void;
};

function EditTaskSchedule(props: EditTaskScheduleProps) {
  const [showModal, setShowModal] = useState(false);
  const [modalStart, setModalStart] = useState(props.start);
  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    ScheduleType.NO_REPEAT
  );
  const [startDateError, setStartDateError] = useState<string | null>(null);

  const { t } = useTranslation();

  const handleListViewItemClick = useCallback(() => {
    if (props.start) {
      setScheduleType(ScheduleType.FUTURE);
    } else {
      setScheduleType(ScheduleType.NO_REPEAT);
    }
    setModalStart(props.start);
    setStartDateError(null);
    setShowModal(true);
  }, [props]);

  const closeAndResetModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(closeAndResetModal);

  const handleConfirmClick = useCallback(() => {
    if (scheduleType === ScheduleType.FUTURE && !modalStart) {
      setStartDateError(t("pleaseSelectDateString"));
    } else {
      closeAndResetModal();
      disableSaveGuard();

      if (scheduleType === ScheduleType.NO_REPEAT) {
        props.onConfirmClick(null);
      } else {
        props.onConfirmClick(modalStart);
      }
    }
  }, [
    scheduleType,
    closeAndResetModal,
    modalStart,
    t,
    props,
    disableSaveGuard,
  ]);

  const handleScheduleTypeChange = useCallback(
    (type: ScheduleType) => {
      setScheduleType(type);
      setStartDateError(null);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleStartChange = useCallback(
    (value: string | null) => {
      setStartDateError(null);
      setModalStart(value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  return (
    <>
      <ListItem
        title={t("scheduleTaskString")}
        icon={CalendarIconPath}
        onClick={handleListViewItemClick}
        disabled={props.disabled}
        emphasised={!!props.start}
        description={getScheduledTaskDescription(props.start, null, null, t)}
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          header={
            <FixaModalHeader
              closeBtnClick={handleCloseClick}
              title={getScheduleTypeTitle(scheduleType, t)}
            />
          }
          footer={
            <FixaModalFooter renderBorder>
              <FixaButton
                text={t("confirmString")}
                type="primary"
                onClick={handleConfirmClick}
                fluid
              />
            </FixaModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <ModalColumn gap="big">
              <FixaListView id="typeSelector" size="small">
                <FixaListViewItem
                  title={t("dontScheduleTaskString")}
                  control="radiobutton"
                  controlProps={{
                    checked: scheduleType === ScheduleType.NO_REPEAT,
                    value: ScheduleType.NO_REPEAT,
                  }}
                  onChange={() =>
                    handleScheduleTypeChange(ScheduleType.NO_REPEAT)
                  }
                />
                <FixaListViewItem
                  title={t("scheduleTaskForFutureString")}
                  control="radiobutton"
                  controlProps={{
                    checked: scheduleType === ScheduleType.FUTURE,
                    value: ScheduleType.FUTURE,
                  }}
                  onChange={() => handleScheduleTypeChange(ScheduleType.FUTURE)}
                />
              </FixaListView>
              {scheduleType === ScheduleType.FUTURE && (
                <FutureTask
                  start={modalStart}
                  startDateError={startDateError}
                  onStartChange={handleStartChange}
                />
              )}
            </ModalColumn>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default EditTaskSchedule;
